import has from 'lodash/has';
import isFunction from 'lodash/isFunction';
import isUndefined from 'lodash/isUndefined';
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {maps as gmaps} from 'google';
import APIUtils from '@spothero/utils/api';
import DOMUtils from '@spothero/utils/dom';
import SegmentUtils from 'utils/segment';
import {GOOGLE_MAPS_LAT_LNG} from 'google-prop-utils';
import NoResultsDialog from './NoResultsDialog';
import SpotMapControls from './SpotMapControls';
import SHGoogleMap from './SHGoogleMap';

class SpotMap extends Component {
    constructor(props) {
        super(props);

        const {
            zoom,
            mapOptions
        } = props;

        this._initialZoom = zoom;
        this._isMapLoaded = false;
        this._isInitialSearchComplete = false;
        this._isIdealDistanceSet = false;
        this._isAutoZooming = false;
        this._isReturningToDestination = false;
        this._isDragging = false;
        this._infoWindowsDisabled = false;
        this._noResultsRetryCount = 0;
        this._mapOptions = {
            disableDefaultUI: true,
            zoomControl: false,
            scrollwheel: false,
            draggable: true,
            styles: [{
                featureType: 'poi',
                stylers: [{
                    visibility: 'off'
                }]
            }, {
                featureType: 'transit.station.rail',
                elementType: 'labels.icon',
                stylers: [{
                    saturation: -100
                }]
            }, {
                featureType: 'transit.station.bus',
                elementType: 'labels.icon',
                stylers: [{
                    saturation: -100
                }]
            }],
            mapTypeId: gmaps.MapTypeId.ROADMAP,
            ...mapOptions
        };
        this._onGoogleMapLoaded = this._onGoogleMapLoaded.bind(this);
        this._onZoomIn = this._onZoomIn.bind(this);
        this._onZoomOut = this._onZoomOut.bind(this);
        this._onDragStart = this._onDragStart.bind(this);
        this._onDragEnd = this._onDragEnd.bind(this);
        this._onMapMoved = this._onMapMoved.bind(this);
        this._onMarkerClick = this._onMarkerClick.bind(this);
        this._onReturnToDestinationClick = this._onReturnToDestinationClick.bind(this);
        this._onGoToCurrentLocationClick = this._onGoToCurrentLocationClick.bind(this);
        this._onNoResultsDialogFilterResultsClick = this._onNoResultsDialogFilterResultsClick.bind(this);
        this._onNoResultsDialogResetFiltersClick = this._onNoResultsDialogResetFiltersClick.bind(this);
        this._onResultsFound = this._onResultsFound.bind(this);
        this._getMarkerLabelText = this._getMarkerLabelText.bind(this);
        this._onSpotMapShimClick = this._onSpotMapShimClick.bind(this);

        this.state = {
            showNoResultsDialog: false,
            isDestinationOutOfBounds: false,
            markers: []
        };
    }

    componentWillReceiveProps(nextProps) {
        const {
            spots,
            kmlMapLayerEnabled
        } = this.props;
        const {
            spots: nextSpots
        } = nextProps;
        const newState = {};

        if (nextProps.isMonthly !== this.props.isMonthly) {
            newState.showNoResultsDialog = false;
        }

        if (spots.length !== nextSpots.length) {
            newState.markers = nextSpots.map(spot => {
                return {
                    spotId: spot.spotId,
                    spot,
                    state: 'default'
                };
            });

            if (spots.length && !kmlMapLayerEnabled && !this._infoWindowsDisabled) {
                this._disableInfoWindows();
            }
        }

        this.setState(newState);
    }

    componentDidUpdate(prevProps, prevState) {
        const {
            spots,
            isMonthly,
            transientStarts,
            transientEnds,
            monthlyStarts,
            isFiltered,
            destination,
            zoom
        } = this.props;
        const {
            zoom: prevZoom
        } = prevProps;

        if (prevProps.destination.latitude !== destination.latitude || prevProps.destination.longitude !== destination.longitude) {
            this._onNewSearch({
                reset: true
            });
        } else if (prevProps.isMonthly !== isMonthly) {
            this._noResultsRetryCount = 0;
            this._onNewSearch({
                reset: true
            });
        } else {
            if ((isMonthly && prevProps.monthlyStarts !== monthlyStarts)
                || !isMonthly && (prevProps.transientStarts !== transientStarts || prevProps.transientEnds !== transientEnds)) {
                this._onNewSearch({
                    dateTimeChanged: true
                });
            }
        }

        if (prevProps.isFiltered !== isFiltered && !spots.length) {
            // typically if you're filtered and search a new area which has no spots we need to make sure
            // that we show the no results dialog on top of the map
            this._onNoResults();
        }

        if (this._isMapLoaded && !this._isIdealDistanceSet) {
            this._isIdealDistanceSet = true;
            this._initialZoom = zoom;
        }

        if (zoom !== prevZoom) {
            if (!this._isReturningToDestination) {
                if (zoom < prevZoom) {
                    this._onDragEnd(true);
                }
            }

            this._isReturningToDestination = false;
        }
    }

    componentWillUnmount() {
        this._cancelSpotsLoader();
    }

    _cancelSpotsLoader() {
        if (this._spotsLoader) {
            this._spotsLoader.cancel();
        }
    }

    _onGoogleMapLoaded(mapComponent) {
        if (mapComponent) {
            const {
                onGoogleMapLoaded
            } = this.props;

            this.googleMap = mapComponent;

            if (onGoogleMapLoaded) {
                onGoogleMapLoaded(this.googleMap);
            }

            if (!this._isMapLoaded) {
                this._setIdealDistance();
            }
        }
    }

    _disableInfoWindows() {
        // Here we redefine set() method.
        // If it is called for map option, we hide InfoWindow, if "noSupress" option isnt true.
        // As Google doesn't know about this option, its InfoWindows will not be opened.
        const set = gmaps.InfoWindow.prototype.set;

        gmaps.InfoWindow.prototype.set = function(key, val) {
            if (key === 'map' && !this.get('noSupress')) { return; }

            set.apply(this, arguments);
        };

        this._infoWindowsDisabled = true;
    }

    _setIdealDistance() {
        const {
            idealDistance,
            overrideIdealZoom,
            destination,
            onZoom,
            spots
        } = this.props;

        this._isMapLoaded = true;

        if (idealDistance && !overrideIdealZoom) {
            const circle = new gmaps.Circle({
                radius: idealDistance,
                center: destination.position
            });

            this._isAutoZooming = true;
            this.googleMap.fitBounds(circle.getBounds());
            onZoom(this.googleMap.getZoom());
        } else {
            this._isAutoZooming = false;
            this._isIdealDistanceSet = true;
        }

        if ((!spots || !spots.length) && !window.location.pathname.match(/parking-near-me/)) {
            this._onNewSearch({
                reset: false
            });
        } else {
            this._isInitialSearchComplete = true;
            this.setState({
                markers: spots.map(spot => {
                    return {
                        spotId: spot.spotId,
                        spot,
                        state: 'default'
                    };
                })
            });
        }
    }

    _checkLoadedSpots() {
        const {
            spots,
            zoom,
            onZoom
        } = this.props;
        let newZoom = zoom;

        // if no results are found
        if (!spots.length) {
            // first time no results are found for each search let's try to zoom out once and search again
            if (this._noResultsRetryCount < 2 && zoom > 12) {
                newZoom = zoom - 1;
                this._isAutoZooming = true;

                this._noResultsRetryCount++;
                onZoom(newZoom);
            } else {
                this._isAutoZooming = false;
                this._noResultsRetryCount = 0;
                this._onNoResults();
            }
        } else {
            this._isAutoZooming = false;
            this._noResultsRetryCount = 0;
            this._onResultsFound();
        }
    }

    _onNewSearch({reset = false, dateTimeChanged = false, autoZoom = false}) {
        if (!this.googleMap) {
            return;
        }

        if (reset) {
            this._onReturnToDestinationClick();
        }

        this._cancelSpotsLoader();

        this._spotsLoader = APIUtils.makeCancelable(
            this.props.loadRadialSpots({
                dateTimeChanged,
                autoZoom
            })
        );
        this._spotsLoader
            .promise
            .then(() => {
                this._checkLoadedSpots();
            })
            .catch(error => {
                if (!has(error, 'isCanceled')) {
                    SegmentUtils.track({
                        event: 'Error Message Displayed',
                        properties: {
                            'error message': 'Error fetching spots',
                            'error screen': 'search',
                            'error action': 'fetching spots'
                        }
                    });
                }
            });

        this._isInitialSearchComplete = true;
    }

    _onZoomIn() {
        const {
            zoom,
            onZoom
        } = this.props;

        if (zoom > 19) { return; }

        onZoom(zoom + 1);
    }

    _onZoomOut() {
        const {
            zoom,
            onZoom
        } = this.props;

        if (zoom < 4) { return; }

        onZoom(zoom - 1);
    }

    _onDragStart() {
        this._isDragging = true;

        if (this._isDraggingTimeout) {
            clearTimeout(this._isDraggingTimeout);
        }
    }

    _onDragEnd(zoomed) {
        if (!this._isInitialSearchComplete) {
            return;
        }

        this._isDraggingTimeout = setTimeout(() => {
            this._isDragging = false;
            this._onMapMoved(zoomed);
        }, 500);
    }

    _onMapMoved(zoomed) {
        const {
            onMapMoved,
            destination,
            loadTiledSpots
        } = this.props;
        const mapBounds = this.googleMap.getBounds();

        this._cancelSpotsLoader();

        if (onMapMoved) {
            onMapMoved({
                zoomed,
                autoZoomed: this._isAutoZooming
            });
        }

        this._spotsLoader = APIUtils.makeCancelable(
            loadTiledSpots(mapBounds)
        );
        this._spotsLoader
            .promise
            .then(() => {
                this._checkLoadedSpots();
            })
            .catch(error => {
                if (!has(error, 'isCanceled')) {
                    SegmentUtils.track({
                        event: 'Error Message Displayed',
                        properties: {
                            'error message': 'Error fetching spots',
                            'error screen': 'search',
                            'error action': 'fetching spots'
                        }
                    });
                }
            });

        // is the destination marker outside of the new map bounds?
        this.setState({
            isDestinationOutOfBounds: !mapBounds.contains(destination.position)
        });
    }

    _onMarkerClick(spotId) {
        if (this._isDragging) { return; }

        this.props.onMarkerClick(spotId);
    }

    _onReturnToDestinationClick() {
        if (this.googleMap) {
            this._isReturningToDestination = true;
            this.googleMap.panTo(this.props.destination.position);
            this.setState({
                isDestinationOutOfBounds: false
            });
            this.props.onZoom(this._initialZoom);
        }
    }

    _onGoToCurrentLocationClick() {
        const {
            findParkingNearMe
        } = this.props;

        if (findParkingNearMe) {
            findParkingNearMe(this.googleMap);
        }
    }

    _onNoResults() {
        DOMUtils.remove('.Tooltip:not(.Tooltip-no-close), .tooltip:not(.tooltip-no-close)');

        this.setState({
            showNoResultsDialog: true
        });
    }

    _onResultsFound() {
        this.setState({
            showNoResultsDialog: false
        });
    }

    _onNoResultsDialogFilterResultsClick() {
        const {
            toggleFiltersClick
        } = this.props;

        this.setState({
            showNoResultsDialog: false
        });

        if (toggleFiltersClick) {
            toggleFiltersClick();
        }
    }

    _onNoResultsDialogResetFiltersClick() {
        const {
            resetFiltersClick
        } = this.props;

        this.setState({
            showNoResultsDialog: false
        });

        if (resetFiltersClick) {
            resetFiltersClick();
        }
    }

    _onSpotMapShimClick(evt) {
        const {
            onShimClick
        } = this.props;

        if (isFunction(onShimClick)) {
            onShimClick(evt);
        }

    }

    _getMarkerLabelText({selectedRate, isDirectLink}) {
        const {
            hideRatesOnPins
        } = this.props;

        if (isDirectLink) {
            return 'Direct';
        }

        if (selectedRate.unavailable) {
            return 'N/A';
        }

        if (hideRatesOnPins) {
            return 'P';
        }

        // if price is over $99, round up pennies to next dollar
        const price = (selectedRate.price.toString().length < 5)
            ? selectedRate.price / 100
            : Math.ceil(selectedRate.price / 100);

        // ensure price is two decimals unless pennies are equal to .00
        return `$${price.toFixed(2).toString().replace('.00', '')}`;
    }

    triggerResize() {
        gmaps.event.trigger(this.googleMap, 'resize');
    }

    render() {
        const {
            isDestinationOutOfBounds,
            showNoResultsDialog,
            markers
        } = this.state;
        const {
            isMobile,
            isFiltered,
            destination,
            mapLoadingIncrement,
            requestIncrements,
            kmlMapLayerURL,
            kmlMapLayerEnabled,
            selectedSpot,
            zoom,
            setShimOverMap
        } = this.props;
        const {
            title: destinationTitle,
            position
        } = destination;
        const classes = classNames(
            'SpotMap',
            {'SpotMap-hidden': !this._isMapLoaded}
        );

        return (
            <div className={classes}>
                {setShimOverMap &&
                    <div
                        className="SpotMap-shim"
                        onClick={this._onSpotMapShimClick}
                    />
                }
                <div className="SpotMap-map">
                    <SHGoogleMap
                        markers={markers}
                        zoom={zoom}
                        position={position}
                        destination={destination}
                        destinationTitle={destinationTitle}
                        kmlMapLayerEnabled={kmlMapLayerEnabled}
                        kmlMapLayerURL={kmlMapLayerURL}
                        mapOptions={this._mapOptions}
                        onDragStart={this._onDragStart}
                        onDragEnd={this._onDragEnd}
                        onMarkerClick={this._onMarkerClick}
                        onGoogleMapLoaded={this._onGoogleMapLoaded}
                        getMarkerLabelText={this._getMarkerLabelText}
                        selectedSpot={selectedSpot}
                    />
                    {!isMobile && showNoResultsDialog &&
                        <NoResultsDialog
                            isFiltered={isFiltered}
                            onHidden={this._onResultsFound}
                            onFilterResults={this._onNoResultsDialogFilterResultsClick}
                            onResetFilters={this._onNoResultsDialogResetFiltersClick}
                        />
                    }
                </div>
                <SpotMapControls
                    isDestinationOutOfBounds={isDestinationOutOfBounds}
                    isFiltered={isFiltered}
                    zoom={zoom}
                    destinationTitle={destinationTitle}
                    onZoomIn={this._onZoomIn}
                    onZoomOut={this._onZoomOut}
                    onGoToCurrentLocation={this._onGoToCurrentLocationClick}
                    onReturnToDestination={this._onReturnToDestinationClick}
                />
                {(mapLoadingIncrement > 0 && !isUndefined(requestIncrements)) &&
                    <div className="SpotMap-loader" style={{width: `${mapLoadingIncrement * (100 / requestIncrements)}%`}} />
                }
            </div>
        );
    }
}

SpotMap.propTypes = {
    mapOptions: PropTypes.object,
    isMobile: PropTypes.bool,
    isFiltered: PropTypes.bool,
    zoom: PropTypes.number,
    onZoom: PropTypes.func,
    onMapMoved: PropTypes.func,
    idealDistance: PropTypes.number,
    overrideIdealZoom: PropTypes.bool,
    destination: PropTypes.shape({
        title: PropTypes.string.isRequired,
        latitude: PropTypes.number.isRequired,
        longitude: PropTypes.number.isRequired,
        position: GOOGLE_MAPS_LAT_LNG
    }).isRequired,
    spots: PropTypes.arrayOf(PropTypes.object).isRequired,
    isMonthly: PropTypes.bool,
    transientStarts: PropTypes.string,
    transientEnds: PropTypes.string,
    monthlyStarts: PropTypes.string,
    onGoogleMapLoaded: PropTypes.func,
    mapLoadingIncrement: PropTypes.number,
    requestIncrements: PropTypes.number,
    onMarkerClick: PropTypes.func.isRequired,
    loadRadialSpots: PropTypes.func.isRequired,
    loadTiledSpots: PropTypes.func.isRequired,
    toggleFiltersClick: PropTypes.func,
    resetFiltersClick: PropTypes.func,
    findParkingNearMe: PropTypes.func,
    kmlMapLayerURL: PropTypes.string,
    kmlMapLayerEnabled: PropTypes.bool,
    hideRatesOnPins: PropTypes.bool,
    setShimOverMap: PropTypes.bool,
    onShimClick: PropTypes.func,
    selectedSpot: PropTypes.object
};

export default SpotMap;



// WEBPACK FOOTER //
// ./spothero/static/main/js/search/components/common/SpotMap.jsx